"use client";
import { Button } from "@padi/ui";
import Image from "next/image";
import { MdStar } from "react-icons/md";
import { UserAvatar } from "@padi/ui/avatar";
import { PiCertificate, PiGlobeHemisphereEast } from "react-icons/pi";
import { cn } from "@padi/lib/utils";
import { BiShieldQuarter } from "react-icons/bi";
import { FaCartShopping } from "react-icons/fa6";
import { IoMdLock } from "react-icons/io";
import { IoShieldHalf } from "react-icons/io5";
import { FaCertificate, FaTruck } from "react-icons/fa";
import { CropSection } from "@padi/shared/crops/_components/crop-listing";
import { useCropDetail } from "../_hooks/use-crop-detail";
import Link from "next/link";
import { OrderCropDialog } from "../_components/order-crop-dialog";
import { useState } from "react";
import { fDMoney, fTon, PER_UNIT, replaceAndCap } from "@padi/lib/utils/format";
import { platform } from "@padi/lib/platform";

export function CropPageContent({ id }: { id: string }) {
  const { data: cropData, isLoading, error } = useCropDetail(id);
  const [mainImg, setMainImg] = useState<string | undefined>(undefined);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error || !cropData) {
    return <div>Crop not found</div>;
  }
  const {
    displayMoney: { pricePerTon },
  } = cropData;

  // Map API data to your UI format
  const crop = {
    id: cropData.id,
    name: cropData.name,
    images: cropData.images.map((img) => img.imageUrl),
    status: cropData.status,
    price: fDMoney(pricePerTon, PER_UNIT),
    priceDetails: "FOB Lagos Port",
    availableQuantity: fTon(cropData.quantityInTon),
    minOrder: fTon(cropData.minOrderInTon),
    origin: cropData.country,
    region: `${cropData.city}, ${cropData.state}`,
    certification: cropData.certificationType
      ? `${cropData.certificationType} Certified`
      : "No Certification",
    deliveryOption: "Exportpadi Shipping",
    deliveryTime: "Depends on location",
    description: cropData.description,
    specifications: Object.entries(cropData.specifications || {}).map(
      ([key, value]) => ({
        label: key
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (str) => str.toUpperCase()),
        value: value as string,
      }),
    ),
  };
  const { farmer } = cropData;

  const MainSection = () => {
    const currentMainImg = mainImg || cropData?.images?.[0]?.imageUrl;
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Section - Image Gallery */}
        <div className="space-y-4">
          <div className="relative w-full h-60 lg:h-[414px] rounded-lg overflow-hidden bg-gray-100">
            <Image
              src={currentMainImg || ""}
              alt={crop.name}
              fill
              className="object-cover"
              priority
              sizes="(max-width: 768px) 100vw, 50vw"
            />
            <div className="absolute top-4 left-4 z-10">
              <span className="bg-primary text-white px-3 py-1 rounded-full text-sm font-medium">
                {replaceAndCap(crop.status)}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-2">
            {crop.images.map((image, index) => (
              <div
                key={index}
                onMouseEnter={() => setMainImg(image)}
                onClick={() => setMainImg(image)}
                className="relative h-20 sm:h-28 lg:h-36 rounded-lg overflow-hidden bg-gray-100 cursor-pointer hover:opacity-80 transition-opacity"
              >
                <Image
                  src={image}
                  alt={`${crop.name} ${index + 2}`}
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 25vw, 12.5vw"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Right Section - Crop Details */}
        <div className="border py-4 px-4 sm:px-6 rounded space-y-4">
          {/* Seller Profile */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <UserAvatar
                src={farmer.profile.avatarUrl}
                name={farmer.profile.displayName}
              />
              <div>
                <p className="font-semibold text-foreground">
                  {farmer.profile.displayName}
                </p>
                <div className="flex items-center gap-2 text-secondary">
                  <span className="text-sm flex items-center">
                    <MdStar size={20} fill={"#E9B306"} />
                    <span>{farmer.totalRatingCount}</span>
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {farmer.viewsCount} Views
                  </span>
                </div>
              </div>
            </div>
            <Link href={`/farms/${farmer.tagName}`}>
              <Button variant="link" className="text-secondary px-0 sm:px-4">
                View Storefront
              </Button>
            </Link>
          </div>

          {/* Price & Quantity */}
          <div className="flex gap-3">
            {[
              {
                label: "Price",
                value: crop.price,
                details: crop.priceDetails,
              },
              {
                label: "Available Quantity",
                value: crop.availableQuantity,
                details: `Min Order: ${crop.minOrder}`,
              },
            ].map((item, index) => (
              <div
                key={index}
                className="bg-[#BDEDE4]/30 rounded-lg px-3 sm:px-6 py-3 flex-1 min-w-0"
              >
                <p className="font-bold text-xs lg:text-base text-foreground mb-1">
                  {item.label}
                </p>
                <p className="sm:text-xl font-black font-montserrat text-primary break-all">
                  {item.value}
                </p>
                <p className="text-xs text-muted-foreground">{item.details}</p>
              </div>
            ))}
          </div>

          {/* Origin/Certification/Delivery */}
          <div className="space-y-3">
            {[
              {
                label: "Origin",
                value: crop.origin,
                icon: <PiGlobeHemisphereEast className="size-4" />,
                origin: true,
              },
              {
                label: "Certification",
                value: crop.certification,
                icon: <PiCertificate className="size-4" />,
              },
              {
                label: "Delivery Option",
                value: crop.deliveryOption,
                textColor: "text-secondary",
                icon: (
                  <Image
                    src="/icons/delivery.svg"
                    alt="shipping"
                    width={20}
                    height={20}
                    className="size-4"
                  />
                ),
                delivery: true,
              },
            ].map((item, index) => (
              <div
                key={index}
                className="bg-white pl-3 sm:pl-4 pb-3 sm:pb-4 flex flex-wrap gap-x-6 gap-y-2 border-b-[1.5px] last:border-b-0"
              >
                <div className="space-y-1.5">
                  <p className="text-sm text-foreground">{item.label}</p>
                  <div className="flex flex-wrap items-center gap-3 text-muted-foreground">
                    <div
                      className={cn(
                        "flex items-center gap-2 border px-2 py-1 rounded-2xl text-sm",
                        item.textColor,
                      )}
                    >
                      {item.icon}
                      <p className="truncate max-w-[140px] sm:max-w-none">
                        {item.value}
                      </p>
                    </div>
                    {item.origin && <p className="text-sm">{crop.region}</p>}
                  </div>
                </div>
                {item.delivery && (
                  <div className="space-y-1.5">
                    <p className="text-sm text-foreground">Delivery time</p>
                    <p className="italic text-muted-foreground text-sm">
                      {crop.deliveryTime}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <OrderCropDialog crop={cropData}>
            <Button variant="secondary" className="w-full" size="xl">
              <FaCartShopping />
              <span>Order Now</span>
            </Button>
          </OrderCropDialog>
        </div>
      </div>
    );
  };

  const DescriptionSection = () => {
    const [isExpanded, setIsExpanded] = useState(false);
    return (
      <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Product Description */}
        <div className="border border-muted-dark rounded-lg p-4 sm:p-8">
          <h3 className="font-bold text-foreground mb-4">
            Product Description
          </h3>
          <div className="mb-6">
            <p
              className={cn(
                "text-muted-foreground transition-all duration-300",
                !isExpanded && "line-clamp-6",
              )}
            >
              {crop.description}
            </p>
            {crop.description && crop.description.length > 200 && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-primary font-medium text-sm mt-2 hover:underline focus:outline-none"
              >
                {isExpanded ? "See Less" : "See More"}
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            {crop.specifications.map((spec, index) => (
              <div
                key={index}
                className="bg-[#BDEDE4]/30 rounded-lg p-3 sm:p-4"
              >
                <p className="text-sm text-primary font-medium mb-1">
                  {spec.label}
                </p>
                <p className="text-lg sm:text-2xl font-semibold text-muted-foreground break-words">
                  {spec.value}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Trade Assurance */}
        <div className="bg-[#BDEDE4]/30 rounded-lg p-4 sm:p-8">
          <h3 className="font-bold text-foreground mb-6">Trade Assurance</h3>
          <div className="space-y-5">
            {[
              {
                label: "Secure Payments",
                value:
                  "Secured payment guaranteed, trade with confidence and trust.",
                icon: <BiShieldQuarter />,
              },
              {
                label: "Escrow Wallet",
                value: "Your payment is protected until you receive the goods",
                icon: <IoMdLock />,
              },
              {
                label: "Logistics Support",
                value: "End-to-end shipping and documentation assistance",
                icon: <FaTruck className="-scale-x-100" />,
              },
              {
                label: "Insurance",
                value:
                  "Insurance protection guaranteed, your peace of mind secured.",
                icon: <IoShieldHalf />,
              },
              {
                label: "Quality Guarantee",
                value: "Pre-shipment inspection and quality verification",
                icon: <FaCertificate />,
              },
            ].map((item, index) => (
              <div
                key={index}
                className="flex items-start gap-3 [&_svg]:text-secondary [&_svg]:size-5 sm:[&_svg]:size-6"
              >
                <div className="shrink-0 mt-0.5">{item.icon}</div>
                <div>
                  <p className="font-bold text-primary">{item.label}</p>
                  <p className="text-muted-foreground text-sm">{item.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen">
      <div className="container px-0 py-8">
        <MainSection />
        <DescriptionSection />
        {platform.isBuyer && (
          <CropSection
            title="Recently Viewed Crops"
            className="bg-[#f2f2f2]/40 mt-16"
            blackTitle
          />
        )}
      </div>
    </div>
  );
}
